import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Ellipse from '@ey/canvascoreservices/Ellipse';
import MotifTooltip from '@ey/motif-react-wrapper/Tooltip';
import PermissionList from '../../../Common/Permission/PermissionList';
import { currentResource, labels } from '../../../../util/uiconstants';
import { useSelector, useDispatch } from 'react-redux';
import { getEmployeeADStatus } from '../../../../actions/admin/engagement/engagementActions';
import { setIsModalOpen } from '../../../../actions/modalstateactions';
import { clearErrors } from '../../../../actions/erroractions';
import {
	userStatusEnum,
	employmentStatuses,
	employmentStatusTypes,
	engagementAccessTypeEnum
} from '../../../../util/uiconstants';
import { EngagementStatus } from '../../../../util/enumerations';
import { MotifIcon, MotifIconButton } from '@ey-xd/motif-react';
import { IconoirEditPencil } from '@ey-xd/motif-icon';

function EngagementUserItem(props) {
	const dispatch = useDispatch();

	const defaultStatus = { key: employmentStatusTypes.DefaultStatus, text: '...' };
	const onErrorStatus = { key: employmentStatusTypes.InActive, text: labels.inactive };

	const employeeADStatuses = useSelector((store) => store.employeeADStatuses);
	const [employeeADStatus, setEmployeeADStatus] = useState(defaultStatus);

	//Microsoft graph call to get AD status
	useEffect(() => {
		dispatch(getEmployeeADStatus(props.data.emailAddress))
			.catch(() => {
				setEmployeeADStatus(onErrorStatus);
				clearError(true);
			})
	}, []);

	// Load AD status in the UI
	useEffect(() => {
		const status = employeeADStatuses[props.data.emailAddress];
		if (
			employmentStatuses.includes(status) &&
			employeeADStatus.key !== employmentStatusTypes.Active
		) {
			setEmployeeADStatus({ key: employmentStatusTypes.Active, text: labels.active });
		}
	}, [employeeADStatuses[props.data.emailAddress]]);

	const clearError = (isModelOpen) => {
		dispatch(clearErrors()).then(() => {
			dispatch(setIsModalOpen(isModelOpen));
		});
	};

	const engagementRoleLabel = useMemo(() => {
		const roleObject = currentResource.rolelistitems?.find(role => role.id === props.data.engagementRoleId);
		return roleObject?.label || '';
	}, []);

	const selectedUserStatus = useMemo(() => {
		if (
			props.data.engagementUserStatusId === userStatusEnum.active &&
			props.data.engagementAccessTypeID === engagementAccessTypeEnum.independenceOnly
		) {
			return labels.independenceOnlyStatus;
		}

		const statusObject = props.userStatusesOptions?.find(option => option.value === props.data.engagementUserStatusId);
		return statusObject?.label || '';
	}, []);

	const selectedIndependenceStatus = useMemo(() => {
		const statusObject = props.independenceStatuseOptions?.find(option => option.value === props.data.independenceStatusId);
		return statusObject?.label || '';
	}, []);

	return (
		<StyledEngagementUserItem>
			<section className="manageaccessitemview motif-body2-default-light">
				<section className="userlistitem userName">
					<Ellipse content={props.userName} tooltip={props.userName} noOfLines={1} />
				</section>
				<section className="userlistitem emailAddress">
					<Ellipse
						content={props.data.emailAddress}
						tooltip={props.data.emailAddress}
						noOfLines={1}
					/>
				</section>
				<section className="userlistitem">
					<Ellipse
						content={engagementRoleLabel}
						tooltip={engagementRoleLabel}
						noOfLines={1}
					/>
				</section>
				<section className="userlistitem gui">
					<Ellipse
						content={props.data.gui}
						tooltip={props.data.gui}
						noOfLines={1}
					/>
				</section>
				<section
					className={`userlistitem status ${props?.data?.isPostArchiveAddition ?
						'postarchiveactive' : 'nopostarchiveactive'}`}
				>
					<Ellipse
						content={selectedUserStatus}
						tooltip={selectedUserStatus}
						noOfLines={1}
					/>
					{props.engagementStatusId === EngagementStatus.Archived && (
						<section className="postarchive">
							<span>
								{props?.data?.isPostArchiveAddition && (
									<div className="motif-badge">
										<span className="motif-badge-number">
											{labels.postArchive}
										</span>{' '}
									</div>
								)}
								{'		'}
							</span>
						</section>
					)}
				</section>

				<section className="userlistitem indstatus">
					<Ellipse
						content={selectedIndependenceStatus}
						tooltip={selectedIndependenceStatus}
						noOfLines={1}
					/>
				</section>
				<section className="userlistitem adstatus">
					{employeeADStatus?.text}
				</section>
				<section className="userlistitem permission">
					<PermissionList
						rbacRoles={props?.data?.userRoles}
						picFlag={props?.data?.hasOverallResponsibility ? true : false}
						userType={null}
					></PermissionList>
				</section>
				<section className="editicon-button">
					<MotifTooltip
						flip
						hideCloseButton
						id={'editbButtonTooltipId'}
						placement={'top'}
						hide={props.permissions}
						trigger={
							<section>
								<MotifIconButton
									type="button"
									aria-label="edit-icon-btn"
									disabled={!props.permissions}
									onClick={() => {
										props.setShowEditUserPermissions(true);
										props.setItemToEditUserPermissions(props.data);
									}}
									className="edit-user-edit-btn"
								>
									<MotifIcon
										name="edit"
										className="edit-icon"
										iconFunction={IconoirEditPencil}
										title={labels.engagementUsersTitleEdit}
										size={24}
									/>
								</MotifIconButton>
							</section>
						}
					>
						<section>{labels.usersEditPermissionToolTip}</section>
					</MotifTooltip>
				</section>
			</section>
		</StyledEngagementUserItem>
	);
}

export default EngagementUserItem;

const StyledEngagementUserItem = styled.section`
	.edit-user-edit-btn {
		background: transparent;
		border: none;
		outline: none;
		transition: 0.2s ease-in-out opacity;
		svg {
			fill: var(--neutrals-1000);
		}

		&[disabled] {
			opacity: 0.2;
			pointer-events: none;
		}
	}

	.editname-icon {
		margin: calc(var(--px-2) * -1) 0 0 0;
		cursor: pointer;
	}
`;
